import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import {
  FormSection,
  MasterDataForm,
} from '../components/master-data/MasterDataForm';
import {
  FilterConfig,
  MasterDataTable,
  TableColumn,
} from '../components/master-data/MasterDataTable';
import { City, District } from '../types/city';

import { Badge } from '@/components/ui/badge';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const CityManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side pagination, filtering, and searching
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>(
    {}
  );
  const [sortConfig, setSortConfig] = useState<{
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }>({});

  // Fetch cities with server-side filtering and pagination
  const {
    data: citiesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      'cities',
      currentPage,
      pageSize,
      searchTerm,
      currentFilters,
      sortConfig,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(
        `/api/master-data/city?${params.toString()}`
      );
      const result = response.data.data || {};
      console.log('City API response:', result);

      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1,
        },
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const cities = citiesResponse?.data || [];
  const pagination = citiesResponse?.pagination;

  // Fetch districts for dropdown
  const { data: districtsData = [] } = useQuery({
    queryKey: ['districts-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/district');
      console.log('CityManagement - Districts API Response:', response.data);
      const districtsData =
        response.data.data?.data?.map((district: any) => ({
          value: district._id, // Use district ID as value
          label: `${district.name} (${district.code})`,
          state: district.state,
        })) || [];
      console.log('CityManagement - Districts for dropdown:', districtsData);
      return districtsData;
    },
  });

  // Fetch states for filtering districts
  const { data: statesData = [] } = useQuery({
    queryKey: ['states-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/state');
      console.log('CityManagement - States API Response:', response.data);
      const statesData =
        response.data.data?.data?.map((state: any) => ({
          value: state._id, // Use state ID as value
          label: `${state.name} (${state.code})`,
          country: state.country,
        })) || [];
      console.log('CityManagement - States for dropdown:', statesData);
      return statesData;
    },
  });

  // Fetch countries for filtering states
  const { data: countriesData = [] } = useQuery({
    queryKey: ['countries-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/country');
      console.log('CityManagement - Countries API Response:', response.data);
      const countriesData =
        response.data.data?.data?.map((country: any) => ({
          value: country._id, // Use country ID as value
          label: `${country.name} (${country.code})`,
        })) || [];
      console.log('CityManagement - Countries for dropdown:', countriesData);
      return countriesData;
    },
  });

  const districtOptions = districtsData;
  const stateOptions = statesData;
  const countryOptions = countriesData;

  // Show debugging info
  React.useEffect(() => {
    console.log('Cities API Response:', citiesResponse);
    console.log('Cities data:', cities);
    console.log('Cities pagination:', pagination);
    console.log('Cities length:', cities.length);
    console.log('Is cities array?', Array.isArray(cities));
    console.log('Districts data:', districtsData);
    console.log('District options:', districtOptions);
    console.log('States data:', statesData);
    console.log('State options:', stateOptions);
    console.log('Countries data:', countriesData);
    console.log('Country options:', countryOptions);

    // Check individual city object
    if (cities.length > 0) {
      console.log('First city object:', cities[0]);
      console.log('City keys:', Object.keys(cities[0]));
    }
  }, [
    citiesResponse,
    cities,
    pagination,
    districtsData,
    districtOptions,
    statesData,
    stateOptions,
    countriesData,
    countryOptions,
  ]);

  // Create city mutation
  const createCityMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/city', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'city'],
      });
      toast({
        title: 'City created',
        description: 'City has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingCity(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error creating city',
          description: error.response?.data?.message || 'Failed to create city',
          variant: 'destructive',
        });
      }
    },
  });

  // Update city mutation
  const updateCityMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await axios.put(`/api/master-data/city/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'city'],
      });
      toast({
        title: 'City updated',
        description: 'City has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingCity(null);
      setServerErrors({});
    },
    onError: (error: any) => {
      // Extract validation errors if available
      const validationErrors = error.response?.data?.errors || {};
      if (Object.keys(validationErrors).length > 0) {
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error updating city',
          description: error.response?.data?.message || 'Failed to update city',
          variant: 'destructive',
        });
      }
    },
  });

  // Delete city mutation
  const deleteCityMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(`/api/master-data/city/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
      queryClient.invalidateQueries({
        queryKey: ['master-data-count', 'city'],
      });
      toast({
        title: 'City deleted',
        description: 'City has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error deleting city',
        description: error.response?.data?.message || 'Failed to delete city',
        variant: 'destructive',
      });
    },
  });

  // Zod schema for validation
  const citySchema = z.object({
    name: z
      .string()
      .min(2, 'City name must be at least 2 characters')
      .max(100, 'City name cannot exceed 100 characters')
      .trim(),
    code: z
      .string()
      .min(2, 'City code must be at least 2 characters')
      .max(10, 'City code cannot exceed 10 characters')
      .regex(
        /^[A-Z0-9_-]+$/,
        'Code can only contain uppercase letters, numbers, underscores and hyphens'
      )
      .trim(),
    district: z.string().min(1, 'District is required'),
    status: z.enum(['Active', 'Inactive'], {
      required_error: 'Status is required',
    }),
  });

  // Enhanced form sections with dropdown populated from API
  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Enter the basic details for the city',
      fields: [
        {
          name: 'name',
          label: 'City Name',
          type: 'text',
          placeholder: 'Enter city name',
          required: true,
        },
        {
          name: 'code',
          label: 'City Code',
          type: 'text',
          placeholder: 'Enter city code (e.g., NYC, LA)',
          required: true,
        },
      ],
    },
    {
      title: 'Location & Classification',
      description: 'Select the district and set the status',
      fields: [
        {
          name: 'district',
          label: 'District',
          type: 'select',
          placeholder: 'Select district',
          required: true,
          options: districtOptions.map((district: any) => ({
            value: district.value,
            label: district.label,
          })),
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: statusOptions,
        },
      ],
    },
  ];

  // Table columns configuration
  const columns: TableColumn<City>[] = [
    {
      key: 'name',
      label: 'City Name',
      sortable: true,
      render: (value, city) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>{city.name}</div>
          <div className='text-xs text-muted-foreground font-mono'>
            {city.code}
          </div>
        </div>
      ),
    },
    {
      key: 'district',
      label: 'District',
      sortable: true,
      render: (value, city) => (
        <div className='space-y-1'>
          <div className='font-medium text-sm'>
            {typeof city.district === 'object'
              ? (city.district as District).name
              : 'Unknown District'}
          </div>
          {typeof city.district === 'object' &&
            (city.district as District).code && (
              <div className='text-xs text-muted-foreground font-mono'>
                {(city.district as District).code}
              </div>
            )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, city) => (
        <Badge
          variant={city.status === 'Active' ? 'default' : 'secondary'}
          className={
            city.status === 'Active'
              ? 'bg-green-100 text-green-800 hover:bg-green-100'
              : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {city.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      hideOnTablet: true,
      render: (value, city) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(city.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      hideOnTablet: true,
      render: (value, city) => (
        <div className='text-sm text-muted-foreground'>
          {new Date(city.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </div>
      ),
    },
  ];

  // Enhanced filter configuration
  const enhancedFilterConfig: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [{ value: 'all', label: 'All Statuses' }, ...statusOptions],
      placeholder: 'Filter by status',
    },
    {
      key: 'country',
      label: 'Country',
      type: 'select',
      options: [
        { value: 'all', label: 'All Countries' },
        ...(countriesData || []).map((country: any) => ({
          value: country.value,
          label: country.label,
        })),
      ],
      placeholder: 'Filter by country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'select',
      options: [
        { value: 'all', label: 'All States' },
        ...(statesData || [])
          .filter((state: any) => {
            // Filter states by selected country if any
            if (!currentFilters.country || currentFilters.country === 'all') {
              return true;
            }
            return typeof state.country === 'string'
              ? state.country === currentFilters.country
              : state.country?._id === currentFilters.country ||
                  state.country?.value === currentFilters.country;
          })
          .map((state: any) => ({
            value: state.value,
            label: state.label,
          })),
      ],
      placeholder: 'Filter by state',
    },
    {
      key: 'district',
      label: 'District',
      type: 'select',
      options: [
        { value: 'all', label: 'All Districts' },
        ...(districtsData || [])
          .filter((district: any) => {
            // Filter districts by selected state if any
            if (!currentFilters.state || currentFilters.state === 'all') {
              return true;
            }
            return typeof district.state === 'string'
              ? district.state === currentFilters.state
              : district.state?._id === currentFilters.state ||
                  district.state?.value === currentFilters.state;
          })
          .map((district: any) => ({
            value: district.value,
            label: district.label,
          })),
      ],
      placeholder: 'Filter by district',
    },
  ];

  // Handler functions
  const handleCreate = () => {
    setEditingCity(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (city: City) => {
    // Transform data for form editing - ensure district is an ID, not an object
    const transformedCity = {
      ...city,
      // Ensure district is the ID value, not the populated object
      district:
        typeof city.district === 'object' && city.district !== null
          ? (city.district as District)._id
          : city.district,
    };
    setEditingCity(transformedCity);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (city: City) => {
    if (window.confirm(`Are you sure you want to delete "${city.name}"?`)) {
      deleteCityMutation.mutate(city._id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCity(null);
    setServerErrors({});
  };

  const handleSubmit = async (formData: any) => {
    try {
      // Validate with Zod
      const validatedData = citySchema.parse(formData);

      if (editingCity) {
        await updateCityMutation.mutateAsync({
          id: editingCity._id,
          data: validatedData,
        });
      } else {
        await createCityMutation.mutateAsync(validatedData);
      }
    } catch (error: any) {
      if (error.errors) {
        // Zod validation errors
        const zodErrors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          zodErrors[err.path[0]] = err.message;
        });
        setServerErrors(zodErrors);
      }
      // Don't throw here, let the mutation handle server errors
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();

      // Add current search and filters to export
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(
        `/api/master-data/city/export?${params.toString()}`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `cities-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: 'Cities data has been exported successfully.',
      });
    } catch (error: any) {
      toast({
        title: 'Export failed',
        description:
          error.response?.data?.message || 'Failed to export cities data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold tracking-tight'>City Management</h1>
          <p className='text-muted-foreground'>
            Manage city master data with comprehensive controls
          </p>
        </div>
      </div>

      <MasterDataTable<City>
        title='Cities'
        description='Manage and configure city information'
        data={cities}
        columns={columns}
        isLoading={isLoading}
        // Pagination
        serverSide={true}
        pagination={pagination}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Search
        currentSearch={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder='Search cities by name or code...'
        // Filtering
        currentFilters={currentFilters}
        onFiltersChange={setCurrentFilters}
        filters={enhancedFilterConfig}
        // Sorting
        currentSort={sortConfig}
        onSortChange={setSortConfig}
        // Actions
        onRefresh={handleRefresh}
        onExport={handleExport}
        onAdd={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        // Permissions
        allowImportExport={true}
      />

      <MasterDataForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        title={editingCity ? 'Edit City' : 'Create City'}
        sections={formSections}
        initialData={editingCity}
        onSubmit={handleSubmit}
        isLoading={createCityMutation.isPending || updateCityMutation.isPending}
        mode={editingCity ? 'edit' : 'create'}
        serverErrors={serverErrors}
      />
    </div>
  );
};

export default CityManagement;
